import { fetchClient } from "@/api/api";
import type { LoginOutput, refreshTokenOutput } from "@/api/custom_types";
import type { ApiErrorType } from "@/api/utils";
import { baseUrlAtom } from "@/store/base_api";
import { currentUserAtom } from "@/store/login";
import { routerAtom } from "@/store/router";
import { MutationObserver } from "@tanstack/react-query";
import { getDefaultStore } from "jotai";
import { getCookie } from "typescript-cookie";
import { RefreshTokenAPIErrorHandler } from "./errorHandler";
import { queryClient, waitForMutation } from "./queryClient";

export class ApiError extends Error {
	status: number;
	url: string;

	constructor(
		message: string,
		public response: Response,
		public body: ApiErrorType | string | null = null,
	) {
		super(message);
		this.response = response;
		this.status = response.status;
		this.url = response.url;
	}

	static async fromRequestResponse(
		message: string,
		request: Request,
		response: Response,
	) {
		const url = new URL(request.url);
		const msg = `${request.method} ${url.pathname} ${response.status} : ${message}`;
		const error = new ApiError(msg, response);
		error.body = await error.response.text().catch(() => null);
		return error;
	}
}

function mergeHeaders(
	headers: HeadersInit,
	newHeaders: HeadersInit,
): HeadersInit {
	const mergedHeaders = new Headers(headers);
	for (const [key, value] of Object.entries(newHeaders)) {
		mergedHeaders.set(key, value);
	}
	return mergedHeaders;
}

function mergeRequestInit(base: RequestInit, apply: RequestInit): RequestInit {
	for (const [key, value] of Object.entries(apply)) {
		if (key === "headers") {
			continue;
		}
		base[key as keyof RequestInit] = value;
	}

	if (apply.headers) {
		if (base.headers) base.headers = mergeHeaders(base.headers, apply.headers);
		else base.headers = apply.headers;
	}

	return base;
}

async function handleUnauthorized(request: Request, response: Response) {
	if (response.status !== 401) return;
	const store = getDefaultStore();
	// ignore logout/refresh/login failures, so we don't get into a loop
	if (
		response.url === `${store.get(baseUrlAtom)}/v1/marketplace/logout` ||
		response.url === `${store.get(baseUrlAtom)}/v1/marketplace/refresh` ||
		response.url === `${store.get(baseUrlAtom)}/v1/marketplace/login`
	) {
		return;
	}

	const accessErrorTypes = [
		"AccessTokenMissing",
		"ExpiredAccessToken",
		"InvalidAccessToken",
	];

	const body = await response.json();
	try {
		if (!accessErrorTypes.includes(body.type)) {
			throw new Error("Can't refresh");
		}
		await safeRefresh();
		await queryClient.cancelQueries();
		await queryClient.invalidateQueries();
		return;
	} catch {
		store.set(currentUserAtom, null);
		getDefaultStore().get(routerAtom).navigate({ replace: true, to: "/login" });
	}
	throw await ApiError.fromRequestResponse("Unauthorized", request, response);
}

const AUTH_HEADERS = {
	"X-Csrf-Token": "X-Csrf-Token",
};

export function buildRequest(url: string, init: RequestInit): Request {
	const headers: HeadersInit = {};
	for (const [key, cookie_name] of Object.entries(AUTH_HEADERS)) {
		const value = getCookie(cookie_name);
		if (value) {
			headers[key] = value;
		}
	}
	const default_init: RequestInit = {
		credentials: "include",
		headers: headers,
	};
	return new Request(url, mergeRequestInit(default_init, init));
}

export async function fetchRequest(request: Request) {
	const response = await fetch(request);
	await handleUnauthorized(request, response);
	return withErrorBody(response);
}

/**
 * openapi-fetch returns `error: undefined` for a failed response with an empty body, so
 * callers checking `if (error)` would treat it as a success. Give it a JSON body instead.
 */
async function withErrorBody(response: Response): Promise<Response> {
	if (response.ok || (await response.clone().text())) return response;

	const headers = new Headers(response.headers);
	headers.set("Content-Type", "application/json");
	headers.delete("Content-Length");
	return new Response(
		JSON.stringify({
			detail: response.statusText || `HTTP ${response.status}`,
		}),
		{ status: response.status, statusText: response.statusText, headers },
	);
}

export function safeRefresh() {
	// If there is already a refresh in progress, wait for it to finish
	const inFlight = queryClient.isMutating({
		mutationKey: ["refresh"],
	});
	const refreshObserver = new MutationObserver<refreshTokenOutput, Error>(
		queryClient,
		{
			mutationKey: ["refresh"],
			mutationFn: async () => {
				const { data, error, response } = await fetchClient.POST(
					"/v1/marketplace/refresh",
					{},
				);
				if (error) {
					const errorMessage = RefreshTokenAPIErrorHandler(response, error);
					throw new ApiError(errorMessage, response);
				}
				return data;
			},
			onSuccess: (data) => {
				const store = getDefaultStore();
				const previousUser = store.get(currentUserAtom);
				const updatedUser: LoginOutput = {
					...data,
					need_follow_otp: previousUser?.need_follow_otp ?? false,
				};
				store.set(currentUserAtom, updatedUser);
			},
		},
	);
	if (inFlight > 0) {
		return waitForMutation(refreshObserver);
	}
	// otherwise, start a new refresh
	return refreshObserver.mutate();
}
